import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import type { SeriesItem, VodItem } from "@hybrid-iptv/types";
import { xtreamVodStreamId } from "@hybrid-iptv/iptv-parser";
import { useCatalogQuery } from "../library/useCatalogQuery.js";
import { PROVIDER_DETAILS_RETRY, fetchProviderMovieDetails } from "../library/vodHooks.js";
import type { CatalogQuery } from "../library/catalogQuery.js";
import { displayTitle, isShelfCandidate } from "./shelfFill.js";
import type { OpenDiscovery } from "./discoveryRows.js";

/**
 * Home's Trending and Popular rows from Open (Section 26), before the device has
 * a discovery file: the catalog alone, so Home is never empty on a first launch
 * (ADR "Home rows and title pages from open data": "With no file ever fetched,
 * rows fall back to the catalog alone"). Step 4 puts the daily file's Trending
 * and Popular in front of this.
 *
 * Entries have Home's list shape, keyed by the provider's TMDB id, so Home's
 * matching, rotation and no-repeat rules treat them exactly as TMDB's; their
 * pictures are the provider's (`posterUrl`, `backdropUrl`).
 */
export interface OpenRowEntry {
  id: number;
  title: string;
  posterUrl?: string;
  backdropUrl?: string;
  vote_average?: number;
  release_date?: string;
}

export interface OpenRowLists {
  trending: OpenRowEntry[];
  popular: OpenRowEntry[];
}

/**
 * How many of the newest titles the rows are picked from, and how many each list
 * carries: the larger of Settings' tiles per row (12 or 24); Home shows the
 * viewer's own number of them.
 */
const POOL = 300;
const ROW = 24;
/** The same quality floor as the genre rows' top-up; Popular asks a little more. */
const TRENDING_FLOOR = 6.5;
const POPULAR_FLOOR = 7;

function entryOf(item: VodItem | SeriesItem): OpenRowEntry {
  const year = "year" in item && item.year ? String(item.year) : undefined;
  const backdrop = "backdropUrl" in item ? item.backdropUrl : undefined;
  return {
    id: item.tmdbId!,
    title: displayTitle(item.rawTitle),
    ...(item.posterUrl ? { posterUrl: item.posterUrl } : {}),
    ...(backdrop ? { backdropUrl: backdrop } : {}),
    ...(item.rating !== undefined ? { vote_average: item.rating } : {}),
    ...(year ? { release_date: year } : {}),
  };
}

/**
 * The first year Trending's window reaches (the discovery job's trendingSince: the
 * current month and the seven before it, owner 2026-10-11). The catalog knows a film's
 * year only, so the window is whole years here: in January it reaches back into last year.
 */
export function trendingSinceYear(now: Date): number {
  return new Date(now.getFullYear(), now.getMonth() - 7, 1).getFullYear();
}

/**
 * Trending: the newest well-rated titles released in Trending's window (owner,
 * 2026-10-11; it was "the current year", which fails in January), newest first; a
 * series in the catalog carries no year, so it is taken as listed.
 * Popular: the best-rated of the rest. Only titles a row could show anyway (a
 * TMDB id, a poster, a Latin title: isShelfCandidate), each TMDB id once.
 */
export function openRowLists(newestFirst: readonly (VodItem | SeriesItem)[], sinceYear: number): OpenRowLists {
  const seen = new Set<number>();
  const candidates: Array<VodItem | SeriesItem> = [];
  for (const item of newestFirst) {
    if (!isShelfCandidate(item) || seen.has(item.tmdbId!)) continue;
    seen.add(item.tmdbId!);
    candidates.push(item);
  }
  const thisYear = (item: VodItem | SeriesItem) => "seasons" in item || (item.year ?? 0) >= sinceYear;
  const trending = candidates.filter((item) => thisYear(item) && (item.rating ?? 0) >= TRENDING_FLOOR).slice(0, ROW);
  const used = new Set(trending.map((item) => item.tmdbId));
  const popular = candidates
    .filter((item) => !used.has(item.tmdbId) && (item.rating ?? 0) >= POPULAR_FLOOR)
    .map((item, index) => ({ item, index }))
    .sort((a, b) => (b.item.rating ?? 0) - (a.item.rating ?? 0) || a.index - b.index)
    .slice(0, ROW)
    .map(({ item }) => item);
  return { trending: trending.map(entryOf), popular: popular.map(entryOf) };
}

/**
 * The lists for one side of Home (films or series): the discovery files' rows when the device
 * has them (discoveryRows.ts, step 4), else from the catalog alone. While the device is still
 * answering whether it has them, nothing is shown yet, so Home never swaps one set of rows
 * for another in front of the viewer (except on the very first launch, when the files arrive).
 */
export function useOpenRowLists(
  sourceId: string,
  media: "movie" | "tv",
  enabled: boolean,
  discovery: OpenDiscovery | null | undefined = null,
) {
  // Series carry no year, so theirs is the provider's own order (newest added first on most panels).
  const query = useMemo<CatalogQuery>(
    () => (media === "tv" ? { kind: "series", sort: "pos", limit: POOL } : { kind: "vod", sort: "year", limit: POOL }),
    [media],
  );
  const fromCatalog = enabled && discovery === null;
  const page = useCatalogQuery<VodItem | SeriesItem>(sourceId, query, fromCatalog && Boolean(sourceId));
  const lists = useMemo(() => {
    if (discovery) return discovery.lists;
    return page.data && fromCatalog ? openRowLists(page.data.items, trendingSinceYear(new Date())) : undefined;
  }, [discovery, page.data, fromCatalog]);
  return { lists, isLoading: enabled && (discovery === undefined || (fromCatalog && page.isLoading)) };
}

/** How many hero candidates are asked about: the hero shows five, and some have no backdrop. */
const HERO_ASK = 8;

/** What the panel told Home about a hero candidate: its picture, and its plot for the banner's text. */
export interface OpenHeroDetails {
  backdropUrl: string;
  overview?: string;
}

/**
 * Backdrops and plots for the hero's films under Open: a film's list entry carries
 * neither, so the panel is asked about the first few candidates only (get_vod_info, the
 * same answer the film's page uses and caches). TMDB id → backdrop URL.
 */
export function useOpenHeroDetails(films: readonly VodItem[], enabled: boolean): Map<number, OpenHeroDetails> {
  const asked = useMemo(() => (enabled ? films.slice(0, HERO_ASK) : []), [films, enabled]);
  const results = useQueries({
    queries: asked.map((movie) => {
      const streamId = xtreamVodStreamId(movie.streamUrl);
      return {
        queryKey: ["movie-details", movie.sourceId, streamId],
        // In the background: a film page a viewer opens meanwhile goes ahead of these.
        queryFn: () => fetchProviderMovieDetails(movie.sourceId, streamId!, "background"),
        enabled: Boolean(streamId),
        staleTime: 1000 * 60 * 60 * 24,
        gcTime: 1000 * 60 * 60 * 24,
        ...PROVIDER_DETAILS_RETRY,
      };
    }),
  });
  // One map per change to the answers, so Home's memos keyed on it stay put.
  const stamp = results.map((result) => result.dataUpdatedAt).join(",");
  return useMemo(() => {
    const map = new Map<number, OpenHeroDetails>();
    asked.forEach((movie, index) => {
      const answer = results[index]?.data;
      const backdrop = answer?.backdropUrl ?? movie.backdropUrl;
      if (backdrop && movie.tmdbId) map.set(movie.tmdbId, { backdropUrl: backdrop, ...(answer?.overview ? { overview: answer.overview } : {}) });
    });
    return map;
    // stamp stands in for results, which is a new array every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asked, stamp]);
}
